'use client';

import { useCallback, useState } from 'react';

import { useSearchParams } from 'next/navigation';

import type { Provider } from '@supabase/supabase-js';

import { isBrowser } from '@kit/shared/utils';
import { If } from '@kit/ui/if';
import { Separator } from '@kit/ui/separator';
import { Trans } from '@kit/ui/trans';

import { useLastAuthMethodPreference } from '../hooks/use-last-auth-method-preference';
import {
  AUTH_METHOD_PRIORITY,
  AuthMethodButton,
  AuthMethodHeader,
  type AuthMethod,
} from './auth-method-button';
import { LastAuthMethodHint } from './last-auth-method-hint';
import { MagicLinkAuthContainer } from './magic-link-auth-container';
import { OauthProviders } from './oauth-providers';
import { OtpSignInContainer } from './otp-sign-in-container';
import { PasswordSignInContainer } from './password-sign-in-container';
import { PhoneSignInContainer } from './phone-sign-in-container';

export function SignInMethodsContainer(props: {
  paths: {
    callback: string;
    joinTeam: string;
    returnPath: string;
  };

  providers: {
    password: boolean;
    magicLink: boolean;
    otp: boolean;
    phone: boolean;
    oAuth: Provider[];
  };

  captchaSiteKey?: string;
  tenantSlug?: string;
}) {
  const enabledMethods = AUTH_METHOD_PRIORITY.filter(
    (method) => props.providers[method],
  );

  // One method renders at a time (previously password + otp identify forms
  // STACKED whenever both providers were on). The active method is DERIVED:
  // an explicit user selection wins; then an ?email arrival pins the first
  // email-capable method (sign-up claim banners and the native claim flow
  // link here with the email prefilled — landing them on the phone form
  // would discard that context); then the last-used method (smart default);
  // otherwise priority order, password first — matching native.
  const preferredMethod = useLastAuthMethodPreference();
  const searchParams = useSearchParams();

  const pinnedMethod = searchParams.get('email')
    ? enabledMethods.find((method) => method !== 'phone')
    : undefined;

  const [selectedMethod, setSelectedMethod] = useState<AuthMethod | null>(
    null,
  );

  const activeMethod =
    selectedMethod ??
    pinnedMethod ??
    (preferredMethod && enabledMethods.includes(preferredMethod)
      ? preferredMethod
      : enabledMethods[0]);

  // Handed to the sign-in containers so the unknown-account card's
  // "try the other method" action can swap the active form in place —
  // parity with the native sign-in page's card.
  const firstEmailMethod = enabledMethods.find((method) => method !== 'phone');

  const switchToPhone = enabledMethods.includes('phone')
    ? () => setSelectedMethod('phone')
    : undefined;

  const switchToEmail = firstEmailMethod
    ? () => setSelectedMethod(firstEmailMethod)
    : undefined;

  const redirectUrl = isBrowser()
    ? new URL(props.paths.callback, window?.location.origin).toString()
    : '';

  const onSignIn = useCallback(() => {
    const returnPath = props.paths.returnPath || '/home';

    // Use hard navigation to ensure proper page refresh on subdomains
    window.location.href = returnPath;
  }, [props.paths.returnPath]);

  const alternativeMethods = enabledMethods.filter(
    (method) => method !== activeMethod,
  );

  // Passed to the active container as `footer` — each container renders it
  // ONLY on its first screen, so the switcher disappears once the user has
  // progressed into a flow (code entry, password step, profile step, …).
  // The hidden marker span doubles as the first-screen signal: AuthBackLink
  // (in the layout shell) is CSS-hidden while it's in the DOM, so the back
  // arrow only appears on screens past the first.
  const alternativesFooter = (
    <>
      <span data-auth-first-screen="" className="hidden" />
      {alternativeMethods.length > 0 ? (
        <div className="flex flex-col gap-y-2.5">
        <div className="relative py-1">
          <div className="absolute inset-0 flex items-center">
            <Separator />
          </div>

          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background text-muted-foreground px-2">
              <Trans
                i18nKey="auth:signInAlternativeMethods"
                defaults="Or sign in with"
              />
            </span>
          </div>
        </div>

          {alternativeMethods.map((method) => (
            <AuthMethodButton
              key={method}
              method={method}
              onSelect={() => setSelectedMethod(method)}
            />
          ))}
        </div>
      ) : null}
    </>
  );

  return (
    <>
      <LastAuthMethodHint />

      {/* OAuth-only tenants mount no method container, so no footer carries
          the first-screen marker — render it directly or the back arrow
          would show (self-referentially) on the very first screen. */}
      <If condition={enabledMethods.length === 0}>
        <span data-auth-first-screen="" className="hidden" />
      </If>

      {/* Which method is active + what happens after Continue — the password
          and email-code forms are otherwise visually identical. */}
      <If condition={enabledMethods.length > 1 && Boolean(activeMethod)}>
        {activeMethod ? <AuthMethodHeader method={activeMethod} /> : null}
      </If>

      <If condition={activeMethod === 'password'}>
        <PasswordSignInContainer
          onSignIn={onSignIn}
          captchaSiteKey={props.captchaSiteKey}
          onSwitchToPhone={switchToPhone}
          footer={alternativesFooter}
        />
      </If>

      <If condition={activeMethod === 'phone'}>
        <PhoneSignInContainer
          shouldCreateUser={false}
          tenantSlug={props.tenantSlug}
          onSwitchToEmail={switchToEmail}
          footer={alternativesFooter}
        />
      </If>

      <If condition={activeMethod === 'otp'}>
        <OtpSignInContainer
          shouldCreateUser={false}
          captchaSiteKey={props.captchaSiteKey}
          footer={alternativesFooter}
        />
      </If>

      <If condition={activeMethod === 'magicLink'}>
        <MagicLinkAuthContainer
          redirectUrl={redirectUrl}
          shouldCreateUser={false}
          captchaSiteKey={props.captchaSiteKey}
        />

        {/* Rendered directly (the stock container takes no footer slot).
            Known imperfection: after the link is sent the container swaps to
            its success alert while this switcher (and the first-screen
            marker) remain — acceptable while no tenant enables magicLink. */}
        {alternativesFooter}
      </If>

      <If condition={props.providers.oAuth.length}>
        <If condition={enabledMethods.length > 0}>
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <Separator />
            </div>

            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background text-muted-foreground px-2">
                <Trans i18nKey="auth:orContinueWith" />
              </span>
            </div>
          </div>
        </If>

        <OauthProviders
          enabledProviders={props.providers.oAuth}
          shouldCreateUser={false}
          paths={{
            callback: props.paths.callback,
            returnPath: props.paths.returnPath,
          }}
        />
      </If>
    </>
  );
}
